/**
 * A planted street tree (lifted from lantern-water's `street-tree.js`): a
 * tapered, bent trunk of jointed limbs with root flares, five forked boughs
 * each carrying three domed leaf cards, and a crown on top. The domed leaf
 * card is the kit's (`street-tree:crown`), shared by every lobe of every tree.
 * The tree draws from its own stream (the prototype seeded it from where the
 * tree stood; here the caller's seed).
 */
import * as THREE from 'three';
import { seededRandom } from '../grid.ts';
import type { Ctx, Vec3 } from './kit.ts';

const crownGeometry = (): THREE.PlaneGeometry => {
  const crown = new THREE.PlaneGeometry(1, 1, 4, 3);
  const pos = crown.getAttribute('position');
  for (let i = 0; i < pos.count; i += 1) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    pos.setZ(i, 0.19 * (1 - 4 * x * x - 4 * y * y));
  }
  crown.computeVertexNormals();
  return crown;
};

/** Builds the tree at (x, 0, z) of `parent`, scaled by `size`. */
export const streetTree = (ctx: Ctx, parent: THREE.Object3D, leafMaterial: THREE.Material, x: number, z: number, size: number, seed: number): THREE.Group => {
  const { m, mesh } = ctx;
  const crown = ctx.kit.shared('street-tree:crown', crownGeometry);
  const random = seededRandom(seed);
  const root = new THREE.Group();
  root.name = 'forked-street-tree';
  root.position.set(x, 0, z);
  root.scale.setScalar(size);
  parent.add(root);
  const up = new THREE.Vector3(0, 1, 0);
  const limb = (a: Vec3, b: Vec3, radiusA: number, radiusB: number): void => {
    const av = new THREE.Vector3(...a);
    const bv = new THREE.Vector3(...b);
    const delta = bv.clone().sub(av);
    const geo = new THREE.CylinderGeometry(radiusB, radiusA, delta.length(), 7);
    // Grain follows the length instead of wrapping a tiny square around it.
    const uv = geo.getAttribute('uv');
    for (let i = 0; i < uv.count; i += 1) uv.setXY(i, uv.getX(i) * radiusA * 5, (uv.getY(i) * delta.length()) / 1.6);
    const mid = av.add(bv).multiplyScalar(0.5);
    const o = mesh(root, geo, m.darkWood, mid.x, mid.y, mid.z);
    o.quaternion.setFromUnitVectors(up, delta.normalize());
  };
  const lean = (random() - 0.5) * 0.2;
  const joints: readonly Vec3[] = [
    [0, 0.07, 0],
    [0.05, 0.64, -0.035],
    [-0.035, 1.38, 0.03],
    [lean, 2.14, -0.04],
    [lean + 0.12, 2.88, 0.02],
  ];
  const radiiA = [0.22, 0.155, 0.125, 0.08];
  const radiiB = [0.155, 0.125, 0.08, 0.045];
  for (let i = 0; i < joints.length - 1; i += 1) limb(joints[i] ?? [0, 0, 0], joints[i + 1] ?? [0, 0, 0], radiiA[i] ?? 0.1, radiiB[i] ?? 0.05);
  for (let i = 0; i < 5; i += 1) {
    const a = i * 2.399;
    limb([0.015, 0.3, 0], [Math.cos(a) * 0.43, 0.075, Math.sin(a) * 0.38], 0.07, 0.018);
  }
  // Five independently forked boughs carry distinct, smaller leaf masses.
  for (let i = 0; i < 5; i += 1) {
    const a = i * 2.399 + 0.25;
    const radius = 0.63 + random() * 0.42;
    const y = 2.65 + random() * 0.75;
    const tip: Vec3 = [Math.cos(a) * radius, y, Math.sin(a) * radius * 0.83];
    const elbow: Vec3 = [tip[0] * 0.62, y - 0.45, tip[2] * 0.64];
    limb([lean * 0.5, 1.55 + i * 0.1, 0], elbow, 0.075, 0.048);
    limb(elbow, tip, 0.048, 0.02);
    for (let j = 0; j < 3; j += 1) {
      const spread = j === 0 ? 0 : 0.39;
      const angle = a + j * 2.5;
      const cx = tip[0] + Math.cos(angle) * spread;
      const cy = tip[1] + (j === 0 ? 0.22 : -0.18 + random() * 0.24);
      const cz = tip[2] + Math.sin(angle) * spread;
      if (j > 0) limb(elbow, [cx, cy - 0.17, cz], 0.033, 0.012);
      const width = 1.5 + random() * 0.35;
      const o = mesh(root, crown, leafMaterial, cx, cy, cz, width, width * (0.87 + random() * 0.16), 1);
      o.rotation.set(-0.3 + (random() - 0.5) * 0.14, (random() - 0.5) * 0.7, (random() - 0.5) * 0.26);
    }
  }
  const top = mesh(root, crown, leafMaterial, lean + 0.08, 3.8, 0, 1.65, 1.62, 1);
  top.rotation.set(-0.3, -0.12, 0.08);
  return root;
};
